import {
    addDeviceLogEntry,
    getPreferredAudioOutputId,
    getPreferredMicrophoneId,
} from 'flow/utils/browser-utils';
import isElectron from 'is-electron';

export const MICROPHONE = 'microphone';
export const CAMERA = 'camera';
export const permissionTypes = {
    DENIED: 'denied',
    GRANTED: 'granted',
    PROMPT: 'prompt',
};
export const MEDIA_DEVICE_TYPES = {
    AUDIO_INPUT: 'audioinput',
    AUDIO_OUTPUT: 'audiooutput',
    VIDEO_INPUT: 'videoinput',
};
const { navigator } = window;

const closeProbeStream = (stream) => {
    if (stream && stream.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
    }
};

/* Opens the devices only to trigger the permission prompt. The caller closes
 * the stream, after anything that needs it open: without a lasting grant
 * (Firefox, Safari "allow once") device labels and ids are only exposed
 * while a track is live. */
const openProbeStream = (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints);

// Opens the devices only to trigger the permission prompt, then closes them again
export const probeMediaPermission = async (constraints) => {
    const stream = await openProbeStream(constraints);
    closeProbeStream(stream);
    return !!stream;
};

const getPermissionStateForFeature = async (feature) => {
    return navigator.permissions
        .query({ name: feature })
        .then((result) => result.state);
};

export const getMediaDeviceTypeSystemPermissionStatusOnMacOS = async (
    mediaType,
) => {
    // On macOS 10.14 and higher, apps that want to access microphone and/or camera need to ask for
    // More info:
    // https://developer.apple.com/documentation/avfoundation/cameras_and_media_capture/requesting_authorization_for_media_capture_on_macos

    if (!isElectron()) {
        // If not Electron, we assume permission on system level is not needed
        return permissionTypes.GRANTED;
    }

    const main = require('@electron/remote');
    const osIsMac = main && main.process.platform === 'darwin';

    if (!osIsMac) {
        // If not macOS, we assume permission on system level is not needed
        return permissionTypes.GRANTED;
    }

    try {
        return await main.systemPreferences.getMediaAccessStatus(mediaType);
    } catch (error) {
        addDeviceLogEntry(
            `Failed getting ${mediaType} access status on macOS system level with error: ${error}. Defaulted to 'granted'`,
        );
        // Defaulting to granted
        return permissionTypes.GRANTED;
    }
};

const askForPermissionToAccessMediaDeviceTypeOnMacOS = async (mediaType) => {
    // On macOS 10.14 and higher, apps that want to access microphone and/or camera need to ask for
    // More info:
    // https://developer.apple.com/documentation/avfoundation/cameras_and_media_capture/requesting_authorization_for_media_capture_on_macos

    if (!isElectron()) {
        return;
    }

    const main = require('@electron/remote');
    const osIsMac = main && main.process.platform === 'darwin';

    if (!osIsMac) {
        // If not macOS, we assume permission on system level is not needed
        return;
    }

    try {
        await main.systemPreferences.askForMediaAccess(mediaType);
    } catch (error) {
        addDeviceLogEntry(
            `Failed getting access to ${mediaType} on macOS system level with error: ${error}`,
        );
    }
};

const getCurrentPermissions = async () => {
    return {
        hasAudioPermission:
            (await getPermissionStateForFeature(MICROPHONE)) ===
                permissionTypes.GRANTED &&
            (await getMediaDeviceTypeSystemPermissionStatusOnMacOS(
                MICROPHONE,
            )) === permissionTypes.GRANTED,
        hasVideoPermission:
            (await getPermissionStateForFeature(CAMERA)) ===
                permissionTypes.GRANTED &&
            (await getMediaDeviceTypeSystemPermissionStatusOnMacOS(CAMERA)) ===
                permissionTypes.GRANTED,
    };
};

const getMicPermissionGrantedModern = async () => {
    return (
        (await getPermissionStateForFeature(MICROPHONE)) ===
            permissionTypes.GRANTED &&
        (await getMediaDeviceTypeSystemPermissionStatusOnMacOS(MICROPHONE)) ===
            permissionTypes.GRANTED
    );
};

/**
 * Get the media devices connected. If permissions are lacking, and the flags
 * are set to true, this method will ask the user for permission to audio and
 * video respectively.
 */
export const askAndGetConnectedMediaDevices = async (
    askForAudio,
    askForVideo,
) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return Promise.reject("'mediaDevices' not supported");
    }

    const permissions = await getMediaPermissions();

    const constraints = {
        audio: !permissions.hasAudioPermission && askForAudio,
        video: !permissions.hasVideoPermission && askForVideo,
    };

    let probeStream;
    if (constraints.audio || constraints.video) {
        try {
            probeStream = await openProbeStream(constraints);
        } catch (error) {
            addDeviceLogEntry(
                `Failed accessing navigator.mediaDevices.getUserMedia microphone with error: ${error}`,
            );
        }
    }

    try {
        if (constraints.audio) {
            await askForPermissionToAccessMediaDeviceTypeOnMacOS(MICROPHONE);
        }

        if (constraints.video) {
            await askForPermissionToAccessMediaDeviceTypeOnMacOS(CAMERA);
        }

        return await navigator.mediaDevices.enumerateDevices();
    } finally {
        closeProbeStream(probeStream);
    }
};

/**
 * Reads the current microphone permission state without prompting the user
 * or opening a media stream — combines the browser-level permission with the
 * macOS system-level (TCC) permission in the desktop app.
 *
 * Browsers that don't support the Permissions API (and therefore can't
 * report `denied` vs `prompt` at all) resolve to `prompt`, so callers that
 * only care about explicit denial fail open rather than mistaking "unknown"
 * for "denied".
 */
export const getMicrophonePermissionState = async () => {
    let browserState;
    try {
        browserState = await getPermissionStateForFeature(MICROPHONE);
    } catch (_e) {
        return permissionTypes.PROMPT;
    }

    if (browserState === permissionTypes.DENIED) {
        return permissionTypes.DENIED;
    }

    const macOSState =
        await getMediaDeviceTypeSystemPermissionStatusOnMacOS(MICROPHONE);
    if (macOSState === permissionTypes.DENIED) {
        return permissionTypes.DENIED;
    }

    return browserState === permissionTypes.GRANTED &&
        macOSState === permissionTypes.GRANTED
        ? permissionTypes.GRANTED
        : permissionTypes.PROMPT;
};

/**
 *
 * @param attemptAskForPermission:
 *      If true we will attempt to prompt the user for microphone permission if it is not
 *      already granted.
 *
 *      If permission has previosuly been denied, the app will not be able to display
 *      the permission prompt. This is because of browser/system restrictions.
 */
export const permissionToMicrophoneIsGranted = async (
    attemptAskForPermission = true,
) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return false;
    }

    const permissionBeforePromptingUserMediaIsGranted =
        await getMicPermissionIsGranted();

    if (permissionBeforePromptingUserMediaIsGranted) {
        return true;
    }

    if (!attemptAskForPermission) {
        return false;
    }

    let probeStream;
    try {
        probeStream = await openProbeStream({ audio: true });
    } catch (error) {
        addDeviceLogEntry(
            `Failed accessing navigator.mediaDevices.getUserMedia microphone with error: ${error}`,
        );
    }

    try {
        await askForPermissionToAccessMediaDeviceTypeOnMacOS(MICROPHONE);

        return await getMicPermissionIsGranted();
    } finally {
        closeProbeStream(probeStream);
    }
};

const getMediaPermissions = async () => {
    /**
     * Checking microphone and camera permissions using navigator.permissions.query will fail on most browsers.
     * This because it, at the time of writing, is a rather new API.
     * If it fails, we check permissions in a more old-fashioned way.
     */

    try {
        return await getCurrentPermissions();
    } catch (_e) {
        return await getCurrentPermissionsLegacy();
    }
};

const getMicPermissionIsGranted = async () => {
    try {
        return await getMicPermissionGrantedModern();
    } catch (_e) {
        return await getMicPermissionGrantedLegacy();
    }
};

const getMicPermissionGrantedLegacy = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasLabel = (device) => !!device.label;

    const audioDevices = filterMediaDevicesBySpecificKind(
        devices,
        'audioinput',
    );
    return audioDevices && audioDevices.every(hasLabel);
};

const getCurrentPermissionsLegacy = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    // If we have permissions for audio or video, their MediaDevicesInfo has a
    // label set. If we don't have permissions, there's no label.
    const hasLabel = (device) => !!device.label;

    const audioDevices = filterMediaDevicesBySpecificKind(
        devices,
        'audioinput',
    );
    const videoDevices = filterMediaDevicesBySpecificKind(
        devices,
        'videoinput',
    );

    const hasAudioPermission = audioDevices && audioDevices.every(hasLabel);
    const hasVideoPermission = videoDevices && videoDevices.every(hasLabel);

    return { hasAudioPermission, hasVideoPermission };
};

export const getConnectedMediaDevices = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return Promise.reject('mediaDevices not supported by browser');
    }
    return navigator.mediaDevices.enumerateDevices();
};

export const filterMediaDevicesBySpecificKind = (
    mediaDevices,
    specifiedDeviceKind,
) => {
    return mediaDevices
        ? mediaDevices.filter((d) => d.kind === specifiedDeviceKind)
        : undefined;
};

/**
 * @param state
 * @param deviceKind
 * @param deviceId
 * @returns mediaDevice of deviceKind with deviceId, or undefined if none exists
 *
 * This function was added because devices of different deviceKinds can have the same id (maaaybe only in the case of 'default')
 */
export const getConnectedMediaDeviceOfKindWithId = (
    mediaDevices,
    deviceKind,
    deviceId,
) => {
    if (!deviceId) {
        return undefined;
    }

    const mediaDevicesOfKind = filterMediaDevicesBySpecificKind(
        mediaDevices,
        deviceKind,
    );
    if (!mediaDevicesOfKind) {
        return undefined;
    }

    return mediaDevicesOfKind.find((d) => d.deviceId === deviceId);
};

export const getMediaConstraints = (
    state,
    shouldUseAudio,
    shouldUseVideo = false,
) => {
    const { headsets } = state;

    const mediaDevices = headsets.get('connectedMediaDevices');
    const preferredMicrophoneId = headsets.get('preferredMicrophoneId');
    const preferredMic = getConnectedMediaDeviceOfKindWithId(
        mediaDevices,
        'audioinput',
        preferredMicrophoneId,
    );

    const preferredMicLabel = preferredMic ? preferredMic.label : '';
    addDeviceLogEntry(
        'Media has preferred microphone device id: ' +
            preferredMicrophoneId +
            ' found: ' +
            !!preferredMic +
            ' name: ' +
            preferredMicLabel,
    );

    /* Pin the processing flags rather than inheriting browser defaults, so what
       we send does not depend on which browser or Electron build is running.
       All three stay on at capture, the browser's own noise suppressor
       included. A model of ours takes over on the live track only once it runs
       (see createNoiseSuppressedTrack). A model that fails to load then leaves
       the call no worse off than with the setting off. */
    const audioDeviceIdObject = {
        deviceId: preferredMic ? { exact: preferredMic.deviceId } : undefined,
        echoCancellation: true,
        autoGainControl: true,
        noiseSuppression: true,
    };

    const preferredCameraId = headsets.get('preferredVideoInputId');
    const preferredCam = getConnectedMediaDeviceOfKindWithId(
        mediaDevices,
        'videoinput',
        preferredCameraId,
    );
    const videoDeviceIdObject = {
        deviceId: preferredCam ? { exact: preferredCam.deviceId } : undefined,
    };

    return {
        audio: shouldUseAudio ? audioDeviceIdObject : false,
        video: shouldUseVideo ? videoDeviceIdObject : false,
    };
};

export const getMediaDevicesLogString = (devices) => {
    return devices && devices.length > 0
        ? devices
              .map((device) => device.label + ' (' + device.deviceId + ')')
              .join('\r\n')
        : 'No devices';
};

/**
 * Attach audio output device to audio element using device/sink ID.
 * https://github.com/webrtc/samples/blob/gh-pages/src/content/devices/input-output/js/main.js
 */
export const setOutputDeviceWithIdToElement = (deviceId, element) => {
    if (!element || typeof element.setSinkId !== 'function') {
        console.warn(
            'setOutputDeviceWithIdToElement -> could not set sinkId due to element or setSinkId function missing.\nelement: ',
            element,
        );
        return;
    }

    if (!deviceId) {
        console.warn('Could not set audio output due to deviceId missing');
        return;
    }

    element
        .setSinkId(deviceId)
        .then(() => {
            console.info(
                'Success setting audio output device with id: ' + deviceId,
            );
        })
        .catch((error) => {
            console.error('setSinkId error: ', error);
        });
};

export const unsetSinkId = async (element) => {
    if (!element) {
        addDeviceLogEntry('Failed to unset sinkId, element not set');
        return;
    }
    if (typeof element.setSinkId !== 'function') {
        addDeviceLogEntry('Failed to unset sinkId, setSinkId function missing');
        return;
    }
    try {
        await element.setSinkId('');
    } catch (e) {
        addDeviceLogEntry('Failed to unset sinkId, error: ', e);
    }
};

export const getNameOfPrimaryAudioOutputDevice =
    (mediaDevices) => (dispatch) => {
        const outputDevice = dispatch(
            getPrimaryAudioOutputDevice(mediaDevices),
        );
        return outputDevice ? outputDevice.label : undefined;
    };

export const getNameOfPrimaryAudioInputDevice =
    (mediaDevices) => (dispatch) => {
        const inputDevice = dispatch(getPrimaryAudioInputDevice(mediaDevices));
        return inputDevice ? inputDevice.label : undefined;
    };

const getPrimaryAudioOutputDevice = (mediaDevices) => (_dispatch, getState) => {
    const { headsets } = getState();
    const preferredAudioOutputId =
        headsets.get('preferredAudioOutputId') || getPreferredAudioOutputId();

    return getPrimaryMediaDeviceOfType(
        mediaDevices,
        MEDIA_DEVICE_TYPES.AUDIO_OUTPUT,
        preferredAudioOutputId,
    );
};

const getPrimaryAudioInputDevice = (mediaDevices) => (_dispatch, getState) => {
    const { headsets } = getState();
    const preferredAudioInputId =
        headsets.get('preferredMicrophoneId') || getPreferredMicrophoneId();

    return getPrimaryMediaDeviceOfType(
        mediaDevices,
        MEDIA_DEVICE_TYPES.AUDIO_INPUT,
        preferredAudioInputId,
    );
};

export const getPrimaryMediaDeviceOfType = (
    mediaDevices,
    deviceType,
    preferredDeviceId,
) => {
    if (preferredDeviceId) {
        const preferredDevice = getConnectedMediaDeviceOfKindWithId(
            mediaDevices,
            deviceType,
            preferredDeviceId,
        );

        if (preferredDevice) {
            return preferredDevice;
        }
    }

    const defaultDevice = getConnectedMediaDeviceOfKindWithId(
        mediaDevices,
        deviceType,
        'default',
    );

    if (defaultDevice) {
        return defaultDevice;
    }

    const connectedDevicesOfDesiredType = filterMediaDevicesBySpecificKind(
        mediaDevices,
        deviceType,
    );

    if (connectedDevicesOfDesiredType && connectedDevicesOfDesiredType[0]) {
        return connectedDevicesOfDesiredType[0];
    }

    return undefined;
};
